import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faEye,
  faEyeSlash,
  faUserShield,
  faShieldAlt,
  faCog,
  faChartLine,
  faUsers,
  faKey,
  faShield,
  faCrown,
  faGem,
  faExclamationTriangle,
  faArrowRight,
  faHome,
  faSignInAlt,
  faUserCog,
} from "@fortawesome/free-solid-svg-icons";
import { saveUserData, redirectToDashboard } from "../../utils/auth";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const loginResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/admin/login`, {
        email: formData.email,
        password: formData.password
      });

      console.log('Login successful:', loginResponse);
      if (loginResponse.status === 200) {
        // Save user data using our utility function
        saveUserData({
          token: loginResponse.data.token,
          role: loginResponse.data.role,
          user: loginResponse.data.user || { role: loginResponse.data.role }
        });

        toast.success('Login successful');

        // Get redirect path if it exists
        const redirectPath = localStorage.getItem('redirectPath') || loginResponse.data.redirectPath;
        localStorage.removeItem('redirectPath'); // Clear it after use

        setTimeout(() => {
          // Redirect to appropriate dashboard based on role
          redirectToDashboard(loginResponse.data.role, navigate, redirectPath);
        }, 50);
      }
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setIsLoading(false);
    }

  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-black flex items-center justify-center p-2 sm:p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            rotate: 360,
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-10 left-10 w-20 h-20 sm:w-32 sm:h-32 bg-red-500/10 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            rotate: -360,
            scale: [1.2, 1, 1.2],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-10 right-10 w-24 h-24 sm:w-40 sm:h-40 bg-red-600/10 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/2 left-1/4 w-16 h-16 sm:w-24 sm:h-24 bg-red-400/5 rounded-full blur-lg"
        />
      </div>

      <div className="w-full max-w-4xl mx-auto relative z-10">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
          <div className="flex flex-col lg:flex-row min-h-[500px] sm:min-h-[600px]">

            {/* Left Side - Enhanced Branding */}
            <div className="hidden lg:block lg:w-2/5 bg-gradient-to-br from-red-600 via-red-700 to-red-800 p-6 relative overflow-hidden">
              {/* Animated Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                  className="absolute top-0 left-0 w-full h-full"
                >
                  <FontAwesomeIcon icon={faCrown} className="text-4xl absolute top-8 left-8" />
                  <FontAwesomeIcon icon={faShieldAlt} className="text-3xl absolute top-16 right-8" />
                  <FontAwesomeIcon icon={faKey} className="text-4xl absolute bottom-16 left-16" />
                  <FontAwesomeIcon icon={faGem} className="text-2xl absolute bottom-8 right-16" />
                </motion.div>
              </div>

              <div className="relative z-10 h-full flex flex-col justify-center items-center text-white">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 1, type: "spring" }}
                  className="mb-8"
                >
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
                    <FontAwesomeIcon icon={faUserShield} className="text-3xl text-white" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                  className="text-center"
                >
                  <h1 className="text-3xl font-bold mb-3 bg-gradient-to-r from-white to-red-100 bg-clip-text text-transparent">
                    Admin Portal
                  </h1>
                  <p className="text-lg text-red-100 mb-6">
                    Secure Administrative Access
                  </p>

                  {/* Feature Cards */}
                  <div className="space-y-4">
                    <motion.div
                      initial={{ x: -50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="flex items-center bg-white/10 rounded-lg p-3 backdrop-blur-sm border border-white/20"
                    >
                      <FontAwesomeIcon icon={faShield} className="text-red-300 mr-3" />
                      <div className="text-left">
                        <div className="font-semibold text-sm">Advanced Security</div>
                        <div className="text-xs text-red-200">Multi-layer protection</div>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ x: -50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.7 }}
                      className="flex items-center bg-white/10 rounded-lg p-3 backdrop-blur-sm border border-white/20"
                    >
                      <FontAwesomeIcon icon={faChartLine} className="text-red-300 mr-3" />
                      <div className="text-left">
                        <div className="font-semibold text-sm">Analytics Dashboard</div>
                        <div className="text-xs text-red-200">Real-time insights</div>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ x: -50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.9 }}
                      className="flex items-center bg-white/10 rounded-lg p-3 backdrop-blur-sm border border-white/20"
                    >
                      <FontAwesomeIcon icon={faUsers} className="text-red-300 mr-3" />
                      <div className="text-left">
                        <div className="font-semibold text-sm">User Management</div>
                        <div className="text-xs text-red-200">Complete control</div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Right Side - Enhanced Login Form */}
            <div className="lg:w-3/5 p-4 sm:p-6 lg:p-8 bg-white/95 backdrop-blur-sm">
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-sm mx-auto"
              >
                {/* Header */}
                <div className="text-center mb-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="w-14 h-14 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg"
                  >
                    <FontAwesomeIcon icon={faCrown} className="text-xl text-white" />
                  </motion.div>

                  <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                    <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                      Admin Login
                    </span>
                  </h2>
                  <div className="w-16 h-0.5 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full mb-3"></div>
                  <p className="text-sm text-gray-600">
                    Enter your credentials to access the administrative dashboard
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg text-sm flex items-center"
                    >
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                      {error}
                    </motion.div>
                  )}

                  {/* Email Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="relative"
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Admin Email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                        <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 text-sm" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all bg-white/50 backdrop-blur-sm text-sm"
                        placeholder="admin@iinsaf.com"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </motion.div>

                  {/* Password Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="relative"
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                        <FontAwesomeIcon icon={faLock} className="text-gray-400 text-sm" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        required
                        className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all bg-white/50 backdrop-blur-sm text-sm"
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-sm" />
                      </button>
                    </div>
                  </motion.div>

                  {/* Options */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        className="h-3.5 w-3.5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                      <label htmlFor="remember-me" className="ml-2 block text-gray-600 text-sm">
                        Remember me
                      </label>
                    </div>
                    <Link
                      to="/admin/forgot-password"
                      className="text-red-600 hover:text-red-500 font-medium transition-colors text-sm"
                    >
                      Forgot Password?
                    </Link>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 px-4 border border-transparent rounded-lg text-white bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center justify-center space-x-2 transition-all transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl text-sm font-semibold"
                    >
                      {isLoading ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                          />
                          <span>Authenticating...</span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faSignInAlt} className="text-sm" />
                          <span>Access Admin Panel</span>
                          <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
                        </>
                      )}
                    </button>
                  </motion.div>
                </form>

                {/* Security Notice */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center text-xs text-gray-600">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-red-500 mr-2 text-sm" />
                    <span>This is a secure administrative area. All activities are logged and monitored.</span>
                  </div>
                </motion.div>

                {/* Quick Links */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                  className="mt-4 pt-4 border-t border-gray-200"
                >
                  <p className="text-center text-xs text-gray-500 mb-3">Quick Access</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Link
                      to="/"
                      className="flex flex-col items-center p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                    >
                      <FontAwesomeIcon icon={faHome} className="text-gray-400 group-hover:text-red-500 mb-1 text-sm" />
                      <span className="text-xs text-gray-600">Home</span>
                    </Link>
                    <Link
                      to="/admin/register"
                      className="flex flex-col items-center p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                    >
                      <FontAwesomeIcon icon={faUserCog} className="text-gray-400 group-hover:text-red-500 mb-1 text-sm" />
                      <span className="text-xs text-gray-600">Register</span>
                    </Link>
                    <Link
                      to="/support"
                      className="flex flex-col items-center p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                    >
                      <FontAwesomeIcon icon={faCog} className="text-gray-400 group-hover:text-red-500 mb-1 text-sm" />
                      <span className="text-xs text-gray-600">Support</span>
                    </Link>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
